import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, MapPin, Crosshair, CheckCircle2, List, Map as MapIcon, Columns2, Copy as CopyIcon, Table as TableIcon, Settings2 } from "lucide-react";
import RequisitosForm, { RequisitosChips } from "@/components/espacios/RequisitosForm";
import { aFormRequisitos, aPayloadRequisitos, hayRequisitos } from "@/lib/requisitosMontaje";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import AutocompleteInput from "@/components/AutocompleteInput";
import EspaciosMap from "@/components/EspaciosMap";
import GroupSortControls from "@/components/GroupSortControls";
import DuplicadosDialog from "@/components/DuplicadosDialog";
import { useTableSort } from "@/lib/useTableSort";
import { broadcastEntityChange } from "@/lib/useEntityRefresh";

const empty = {
  nombre: "", cadena_id: "", direccion: "", ciudad: "",
  provincia: "", cp: "", pais: "España", tipo: "Perfumería",
  dimensiones: "", contacto_local: "", telefono_local: "", horario: "", notas: "",
  zonas: [],
  latitud: "", longitud: "", geocode_manual: false,
};

const TIPOS_ESPACIO = [
  "Perfumería", "Farmacia", "Tienda", "Centro Comercial",
  "Supermercado", "Restaurante", "Hotel", "Clínica",
  "Oficina", "Stand", "Escaparate", "Showroom",
  "Almacén/Producción", "Otro",
];

const PAISES = ["España", "Portugal", "Andorra", "Francia"];

function nuevaZona() {
  return { id: Math.random().toString(36).slice(2, 10), nombre: "", ancho_cm: "", alto_cm: "", descripcion: "" };
}

export default function EspaciosPage() {
  const [items, setItems] = useState([]);
  const [cadenas, setCadenas] = useState([]);
  const [provinciasOpts, setProvinciasOpts] = useState([]);
  const [ciudadesOpts, setCiudadesOpts] = useState([]);
  const [filterCadena, setFilterCadena] = useState("todas");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("dnf-espacios-viewmode") || "split"); // "list" | "map" | "split" | "table"
  useEffect(() => { localStorage.setItem("dnf-espacios-viewmode", viewMode); }, [viewMode]);
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem("dnf-espacios-groupby") || "none");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("dnf-espacios-sortby") || "nombre");
  const [sortDir, setSortDir] = useState(() => localStorage.getItem("dnf-espacios-sortdir") || "asc");
  useEffect(() => { localStorage.setItem("dnf-espacios-groupby", groupBy); }, [groupBy]);
  useEffect(() => { localStorage.setItem("dnf-espacios-sortby", sortBy); }, [sortBy]);
  useEffect(() => { localStorage.setItem("dnf-espacios-sortdir", sortDir); }, [sortDir]);
  const [open, setOpen] = useState(false);
  const [duplicadosOpen, setDuplicadosOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const load = () => Promise.all([api.get("/espacios"), api.get("/cadenas")])
    .then(([e, cd]) => { setItems(e.data); setCadenas(cd.data); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/normalizacion/provincias").then(({ data }) => setProvinciasOpts(data.todas || [])).catch(() => {});
    api.get("/normalizacion/ciudades").then(({ data }) => setCiudadesOpts(data.canonicas || [])).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it, zonas: it.zonas || [] }); setOpen(true); setZonaReqAbierta(null); };
  const [zonaReqAbierta, setZonaReqAbierta] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    // Normalizar zonas: parsear números
    payload.zonas = payload.zonas
      .filter((z) => z.nombre?.trim())
      .map((z) => ({
        id: z.id,
        nombre: z.nombre,
        ancho_cm: z.ancho_cm ? parseFloat(z.ancho_cm) : null,
        alto_cm: z.alto_cm ? parseFloat(z.alto_cm) : null,
        descripcion: z.descripcion || null,
        requisitos_montaje: aPayloadRequisitos(z.requisitos_montaje),
      }));
    // Lat/lon a float (o null)
    payload.latitud = payload.latitud !== "" && payload.latitud !== null ? parseFloat(payload.latitud) : null;
    payload.longitud = payload.longitud !== "" && payload.longitud !== null ? parseFloat(payload.longitud) : null;
    Object.keys(payload).forEach((k) => payload[k] === "" && (payload[k] = null));
    if (editing) {
      await api.patch(`/espacios/${editing.id}`, payload);
    } else {
      try {
        await api.post("/espacios", payload);
      } catch (err) {
        const det = err?.response?.data?.detail;
        if (err?.response?.status === 409 && det?.error === "ESPACIO_DUPLICADO") {
          const ex = det.espacio_existente;
          const msg = `${det.mensaje}\n\nDatos del espacio existente:\n• ${ex.nombre}\n• ${ex.ciudad || "—"}${ex.direccion ? "\n• " + ex.direccion : ""}\n\n¿Quieres crear igualmente uno nuevo? (Cancelar = abre el existente).`;
          if (window.confirm(msg)) {
            // Forzar creación con flag _force_create (lo añadimos al backend si quieres)
            // De momento, simple: añadimos sufijo único al nombre para sortear el check
            await api.post("/espacios", { ...payload, nombre: payload.nombre + " (2)" });
            toast.info("Se ha creado con sufijo '(2)' para diferenciarlo.");
          } else {
            // Abrir el existente para edición
            try {
              const r = await api.get(`/espacios/${ex.id}`);
              setEditing(r.data);
              return;  // dejar el dialog abierto en modo edición
            } catch { /* ignore */ }
          }
        } else {
          throw err;
        }
      }
    }
    broadcastEntityChange("espacio", { action: editing ? "update" : "create" });
    setOpen(false); load();
  };

  const filtered = filterCadena === "todas"
    ? items
    : items.filter((e) => e.cadena_id === filterCadena);

  const cadName = (id) => cadenas.find((c) => c.id === id)?.nombre || "Sin cadena";
  const sortedFiltered = [...filtered].sort((a, b) => {
    let va, vb;
    switch (sortBy) {
      case "nombre":    va = (a.nombre || "").toLowerCase(); vb = (b.nombre || "").toLowerCase(); break;
      case "ciudad":    va = (a.ciudad || "").toLowerCase(); vb = (b.ciudad || "").toLowerCase(); break;
      case "provincia": va = (a.provincia || "").toLowerCase(); vb = (b.provincia || "").toLowerCase(); break;
      case "cadena":    va = cadName(a.cadena_id).toLowerCase(); vb = cadName(b.cadena_id).toLowerCase(); break;
      case "tipo":      va = a.tipo || ""; vb = b.tipo || ""; break;
      default:          va = (a.nombre || "").toLowerCase(); vb = (b.nombre || "").toLowerCase();
    }
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const groupKeyOf = (e) => {
    switch (groupBy) {
      case "cadena":    return cadName(e.cadena_id);
      case "provincia": return e.provincia || "Sin provincia";
      case "ciudad":    return e.ciudad || "Sin ciudad";
      case "tipo":      return e.tipo || "Sin tipo";
      default:          return null;
    }
  };
  const grouped = (() => {
    if (groupBy === "none") return [{ key: null, items: sortedFiltered }];
    const map = new Map();
    for (const x of sortedFiltered) {
      const k = groupKeyOf(x) || "—";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(x);
    }
    return Array.from(map.entries()).sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([key, items]) => ({ key, items }));
  })();

  return (
    <div className="p-8 max-w-[1800px]">
      <PageHeader
        title="Espacios"
        subtitle="Tiendas, escaparates y ubicaciones de montaje (con zonas y medidas)"
        action={
          <div className="flex gap-2">
            <Button
              onClick={() => setDuplicadosOpen(true)}
              data-testid="buscar-duplicados-btn-espacios"
              variant="outline"
              className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900"
            >
              <CopyIcon className="w-4 h-4 mr-2" /> Buscar duplicados
            </Button>
            <Button onClick={openCreate} data-testid="new-espacio-btn" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
              <Plus className="w-4 h-4 mr-2" /> Nuevo espacio
            </Button>
          </div>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-3 justify-between">
        <Select value={filterCadena} onValueChange={setFilterCadena}>
          <SelectTrigger className="w-64 bg-zinc-900/70 border-zinc-800"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
            <SelectItem value="todas">Todas las cadenas</SelectItem>
            {cadenas.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Toggle vista lista/mapa/split */}
        <div className="inline-flex rounded-md border border-zinc-800 bg-zinc-900/70 p-0.5" data-testid="espacios-view-toggle">
          <button
            type="button"
            onClick={() => setViewMode("list")}
            data-testid="espacios-view-list"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded transition ${
              viewMode === "list"
                ? "bg-amber-500 text-zinc-900 font-semibold"
                : "text-zinc-400 hover:text-zinc-100"
            }`}
          >
            <List className="w-3.5 h-3.5" /> Lista
          </button>
          <button
            type="button"
            onClick={() => setViewMode("split")}
            data-testid="espacios-view-split"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded transition ${
              viewMode === "split"
                ? "bg-amber-500 text-zinc-900 font-semibold"
                : "text-zinc-400 hover:text-zinc-100"
            }`}
          >
            <Columns2 className="w-3.5 h-3.5" /> Split
          </button>
          <button
            type="button"
            onClick={() => setViewMode("map")}
            data-testid="espacios-view-map"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded transition ${
              viewMode === "map"
                ? "bg-amber-500 text-zinc-900 font-semibold"
                : "text-zinc-400 hover:text-zinc-100"
            }`}
          >
            <MapIcon className="w-3.5 h-3.5" /> Mapa
          </button>
          <button
            type="button"
            onClick={() => setViewMode("table")}
            data-testid="espacios-view-table"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded transition ${
              viewMode === "table"
                ? "bg-amber-500 text-zinc-900 font-semibold"
                : "text-zinc-400 hover:text-zinc-100"
            }`}
          >
            <TableIcon className="w-3.5 h-3.5" /> Tabla
          </button>
        </div>
      </div>

      {/* Agrupar / Ordenar */}
      <div className="flex items-center justify-between mb-5">
        <GroupSortControls
          testIdPrefix="espacios"
          groupBy={groupBy} setGroupBy={setGroupBy}
          groupOptions={[
            { value: "cadena", label: "Por cadena" },
            { value: "provincia", label: "Por provincia" },
            { value: "ciudad", label: "Por ciudad" },
            { value: "tipo", label: "Por tipo" },
          ]}
          sortBy={sortBy} setSortBy={setSortBy}
          sortOptions={[
            { value: "nombre", label: "Nombre" },
            { value: "ciudad", label: "Ciudad" },
            { value: "provincia", label: "Provincia" },
            { value: "cadena", label: "Cadena" },
            { value: "tipo", label: "Tipo" },
          ]}
          sortDir={sortDir} setSortDir={setSortDir}
          onReset={() => { setGroupBy("none"); setSortBy("nombre"); setSortDir("asc"); }}
        />
        <div className="text-[10px] text-zinc-500" data-testid="espacios-count">
          {sortedFiltered.length} {sortedFiltered.length === 1 ? "espacio" : "espacios"}
        </div>
      </div>

      {/* Layout según viewMode */}
      {viewMode === "table" ? (
        <EspaciosTable items={sortedFiltered} cadenas={cadenas} openEdit={openEdit} reload={load} />
      ) : viewMode === "map" ? (
        <EspaciosMap espacios={sortedFiltered} cadenas={cadenas} height={620} />
      ) : viewMode === "split" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="lg:max-h-[calc(100dvh-260px)] lg:overflow-y-auto lg:pr-2 space-y-3" data-testid="espacios-split-list">
            {grouped.map((grp, gi) => (
              <React.Fragment key={grp.key ?? `grp-${gi}`}>
                {grp.key !== null && (
                  <div data-testid={`espacios-group-${grp.key}`}
                    className="text-[10px] uppercase tracking-[0.16em] text-amber-300/80 font-semibold pt-2 pb-1 flex items-center justify-between">
                    <span>{grp.key}</span>
                    <span className="text-zinc-500">{grp.items.length}</span>
                  </div>
                )}
                {grp.items.map((e) => {
                  const cad = cadenas.find((c) => c.id === e.cadena_id);
                  return <EspacioCard key={e.id} e={e} cad={cad} openEdit={openEdit} reload={load} compact />;
                })}
              </React.Fragment>
            ))}
            {sortedFiltered.length === 0 && <div className="text-center py-12 text-zinc-500">Sin espacios.</div>}
          </div>
          <div className="lg:sticky lg:top-4 self-start">
            <EspaciosMap espacios={sortedFiltered} cadenas={cadenas} height={620} />
          </div>
        </div>
      ) : (
        <div>
          {grouped.map((grp, gi) => (
            <React.Fragment key={grp.key ?? `grp-${gi}`}>
              {grp.key !== null && (
                <div data-testid={`espacios-group-${grp.key}`}
                  className="text-[10px] uppercase tracking-[0.16em] text-amber-300/80 font-semibold pt-3 pb-2 flex items-center justify-between">
                  <span>{grp.key}</span>
                  <span className="text-zinc-500">{grp.items.length}</span>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-3">
                {grp.items.map((e) => {
                  const cad = cadenas.find((c) => c.id === e.cadena_id);
                  return <EspacioCard key={e.id} e={e} cad={cad} openEdit={openEdit} reload={load} />;
                })}
              </div>
            </React.Fragment>
          ))}
          {sortedFiltered.length === 0 && <div className="text-center py-12 text-zinc-500">Sin espacios.</div>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar espacio" : "Nuevo espacio"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nombre *</Label>
                <Input required value={form.nombre} data-testid="form-espacio-nombre"
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div className="col-span-2"><Label>Cadena</Label>
                <Select value={form.cadena_id} onValueChange={(v) => setForm({ ...form, cadena_id: v })}>
                  <SelectTrigger data-testid="form-espacio-cadena" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {cadenas.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-zinc-500 mt-1">Un espacio puede recibir varios clientes. La facturación se vincula a la petición, no al espacio.</div>
              </div>
              <div><Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="form-espacio-tipo" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {TIPOS_ESPACIO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label>País</Label>
                <Select value={form.pais || "España"} onValueChange={(v) => setForm({ ...form, pais: v })}>
                  <SelectTrigger data-testid="form-espacio-pais" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {PAISES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="col-span-2"><Label>Dirección</Label>
                <AddressAutocomplete value={form.direccion}
                  onChange={(v) => setForm({ ...form, direccion: v })}
                  onSelect={(loc) => setForm({
                    ...form,
                    direccion: loc.direccion,
                    ciudad: loc.ciudad || form.ciudad,
                    provincia: loc.provincia || form.provincia,
                    cp: loc.cp || form.cp,
                    latitud: loc.latitud,
                    longitud: loc.longitud,
                    geocode_manual: false,
                  })}
                  placeholder="ej. Calle Sierpes 30, Sevilla"
                  data-testid="form-espacio-direccion"
                  className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Ciudad</Label>
                <AutocompleteInput value={form.ciudad}
                  onChange={(v) => setForm({ ...form, ciudad: v })}
                  options={ciudadesOpts}
                  placeholder="ej. Sevilla"
                  testId="form-espacio-ciudad"
                  className="" /></div>
              <div><Label>Provincia</Label>
                <AutocompleteInput value={form.provincia}
                  onChange={(v) => setForm({ ...form, provincia: v })}
                  options={provinciasOpts}
                  placeholder="ej. Sevilla"
                  testId="form-espacio-provincia"
                  className="" /></div>
              <div><Label>Código postal</Label>
                <Input value={form.cp} onChange={(e) => setForm({ ...form, cp: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Contacto local</Label>
                <Input value={form.contacto_local} onChange={(e) => setForm({ ...form, contacto_local: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Teléfono local</Label>
                <Input value={form.telefono_local} onChange={(e) => setForm({ ...form, telefono_local: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div className="col-span-2"><Label>Horario</Label>
                <Input value={form.horario} onChange={(e) => setForm({ ...form, horario: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div className="col-span-2"><Label>Notas</Label>
                <Textarea value={form.notas} rows={2} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
            </div>

            {/* GEOLOCALIZACIÓN */}
            <div className="bg-cyan-500/5 border border-cyan-500/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-cyan-300 flex items-center gap-1.5">
                  <Crosshair className="w-3.5 h-3.5" /> Geolocalización (mapa)
                </Label>
                {editing && (
                  <Button type="button" size="sm" variant="ghost"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/espacios/${editing.id}/geocode`);
                        setForm((f) => ({ ...f, latitud: data.latitud, longitud: data.longitud, geocode_manual: false }));
                        toast.success("Localizado correctamente");
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "No se encontró la dirección");
                      }
                    }}
                    data-testid="localizar-btn"
                    className="text-cyan-300 hover:text-cyan-200 h-7 px-2 text-xs">
                    <Crosshair className="w-3 h-3 mr-1" /> Localizar automáticamente
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-[10px] text-zinc-500">Latitud</Label>
                  <Input type="number" step="0.000001" value={form.latitud}
                    onChange={(e) => setForm({ ...form, latitud: e.target.value, geocode_manual: true })}
                    className="bg-zinc-900 border-zinc-800 h-8 text-xs" /></div>
                <div><Label className="text-[10px] text-zinc-500">Longitud</Label>
                  <Input type="number" step="0.000001" value={form.longitud}
                    onChange={(e) => setForm({ ...form, longitud: e.target.value, geocode_manual: true })}
                    className="bg-zinc-900 border-zinc-800 h-8 text-xs" /></div>
                <div className="flex items-end">
                  {form.latitud && form.longitud && (
                    <a href={`https://www.google.com/maps?q=${form.latitud},${form.longitud}`}
                      target="_blank" rel="noreferrer"
                      className="text-[11px] text-cyan-400 hover:text-cyan-300">Ver en Google Maps ↗</a>
                  )}
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 mt-1.5">
                {form.geocode_manual
                  ? "🔒 Coordenadas fijadas a mano (no se actualizarán al cambiar la dirección)"
                  : "Se actualizan automáticamente cuando cambia la dirección"}
              </div>
            </div>

            {/* ZONAS */}
            <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-amber-300">Zonas del espacio (con medidas)</Label>
                <Button type="button" size="sm" variant="ghost"
                  onClick={() => setForm({ ...form, zonas: [...form.zonas, nuevaZona()] })}
                  data-testid="add-zona-btn"
                  className="text-amber-300 hover:text-amber-200 h-7 px-2 text-xs">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Añadir zona
                </Button>
              </div>
              {form.zonas.length === 0 && (
                <div className="text-xs text-zinc-500 italic">Sin zonas. Si el espacio tiene varios sitios con medidas (escaparate izq/dch, podium, etc.), añádelos.</div>
              )}
              <div className="space-y-2">
                {form.zonas.map((z, idx) => (
                  <div key={z.id} className="space-y-1" data-testid={`zona-row-${idx}`}>
                  <div className="grid grid-cols-[1.5fr_0.7fr_0.7fr_2fr_auto_auto] gap-2 items-end">
                    <div>
                      {idx === 0 && <Label className="text-[10px] text-zinc-500">Nombre</Label>}
                      <Input placeholder='ej. "Lado izquierdo escaparate"' value={z.nombre}
                        onChange={(e) => {
                          const newZonas = [...form.zonas]; newZonas[idx].nombre = e.target.value;
                          setForm({ ...form, zonas: newZonas });
                        }}
                        className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
                    </div>
                    <div>
                      {idx === 0 && <Label className="text-[10px] text-zinc-500">Ancho (cm)</Label>}
                      <Input type="number" step="0.1" value={z.ancho_cm}
                        onChange={(e) => {
                          const newZonas = [...form.zonas]; newZonas[idx].ancho_cm = e.target.value;
                          setForm({ ...form, zonas: newZonas });
                        }}
                        className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
                    </div>
                    <div>
                      {idx === 0 && <Label className="text-[10px] text-zinc-500">Alto (cm)</Label>}
                      <Input type="number" step="0.1" value={z.alto_cm}
                        onChange={(e) => {
                          const newZonas = [...form.zonas]; newZonas[idx].alto_cm = e.target.value;
                          setForm({ ...form, zonas: newZonas });
                        }}
                        className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
                    </div>
                    <div>
                      {idx === 0 && <Label className="text-[10px] text-zinc-500">Descripción</Label>}
                      <Input placeholder="opcional" value={z.descripcion || ""}
                        onChange={(e) => {
                          const newZonas = [...form.zonas]; newZonas[idx].descripcion = e.target.value;
                          setForm({ ...form, zonas: newZonas });
                        }}
                        className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
                    </div>
                    <Button type="button" size="sm" variant="ghost" title="Requisitos de montaje de esta zona (personas, dificultad, medio, horario, instrucciones)"
                      onClick={() => setZonaReqAbierta(zonaReqAbierta === z.id ? null : z.id)} data-testid={`zona-req-btn-${idx}`}
                      className={`h-8 w-8 p-0 ${hayRequisitos(z.requisitos_montaje) ? "text-amber-300" : "text-zinc-500"} hover:text-amber-200`}>
                      <Settings2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button type="button" size="sm" variant="ghost"
                      onClick={() => setForm({ ...form, zonas: form.zonas.filter((_, i) => i !== idx) })}
                      className="text-zinc-500 hover:text-red-400 h-8 w-8 p-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {zonaReqAbierta !== z.id && hayRequisitos(z.requisitos_montaje) && (
                    <div className="text-[11px] text-zinc-400 pl-1 flex items-center gap-1.5 flex-wrap" data-testid={`zona-req-resumen-${idx}`}>
                      <span className="text-amber-300/80">Requisitos:</span> {z.requisitos_montaje?.material_habitual && <span className="text-zinc-200">{z.requisitos_montaje.material_habitual}</span>} <RequisitosChips req={aPayloadRequisitos(z.requisitos_montaje)} testId={`zona-req-chips-${idx}`} />
                    </div>
                  )}
                  {zonaReqAbierta === z.id && (
                    <div className="rounded-lg border border-amber-500/30 bg-zinc-950/60 p-2" data-testid={`zona-req-panel-${idx}`}>
                      <div className="text-[11px] text-amber-200 mb-1.5">Requisitos de montaje · <span className="text-zinc-400">conocimiento recurrente de esta zona; al crear una línea se copia como snapshot editable</span></div>
                      <RequisitosForm value={aFormRequisitos(z.requisitos_montaje)} testId={`zona-req-form-${idx}`}
                        onChange={(v) => { const newZonas = [...form.zonas]; newZonas[idx] = { ...newZonas[idx], requisitos_montaje: v }; setForm({ ...form, zonas: newZonas }); }} />
                    </div>
                  )}
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="bg-transparent border-zinc-700">Cancelar</Button>
              <Button type="submit" data-testid="form-espacio-submit" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <DuplicadosDialog
        open={duplicadosOpen}
        onOpenChange={setDuplicadosOpen}
        entidad="espacios"
        fetchPath="/espacios/duplicados-candidatos"
        deletePath="/espacios"
        onEdit={(it) => {
          // Buscar el objeto completo en la lista y abrir el form
          const full = items.find((x) => x.id === it.id);
          if (full) { setDuplicadosOpen(false); openEdit(full); }
          else toast.info("Refresca la lista, el espacio no está cargado.");
        }}
        onAfterChange={load}
        renderItem={(it) => (
          <div className="text-sm">
            <div className="font-semibold text-zinc-100">{it.nombre}</div>
            <div className="text-xs text-zinc-400 mt-0.5">
              {[it.ciudad, it.provincia].filter(Boolean).join(", ") || "—"}
            </div>
            {it.direccion && <div className="text-[11px] text-zinc-500 truncate">{it.direccion}</div>}
          </div>
        )}
      />
    </div>
  );
}

// ============ ESPACIO CARD ============
function EspacioCard({ e, cad, openEdit, reload, compact = false }) {
  return (
    <div data-testid={`espacio-card-${e.id}`}
      className={`bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-amber-500/40 transition-colors ${compact ? "p-3" : "p-5"}`}>
      <div className="flex items-center gap-2 mb-1">
        <MapPin className="w-4 h-4 text-amber-400" />
        <div className={`font-semibold truncate ${compact ? "text-sm" : "text-base"}`}>{e.nombre}</div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {cad && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold"
            style={{ backgroundColor: (cad.color || "#f59e0b") + "20", color: cad.color || "#f59e0b", borderColor: (cad.color || "#f59e0b") + "60", borderWidth: 1 }}>
            {cad.nombre}
          </span>
        )}
      </div>
      <div className="space-y-1 text-xs text-zinc-400">
        {e.tipo && <div><span className="text-zinc-500">Tipo:</span> {e.tipo}</div>}
        {(e.ciudad || e.provincia) && <div><span className="text-zinc-500">Ubicación:</span> {[e.ciudad, e.provincia, e.cp].filter(Boolean).join(", ")}</div>}
        {e.direccion && <div className="truncate"><span className="text-zinc-500">Dir.:</span> {e.direccion}</div>}
        {!compact && e.contacto_local && <div><span className="text-zinc-500">Contacto:</span> {e.contacto_local}</div>}
        {!compact && e.telefono_local && <div><span className="text-zinc-500">Tel:</span> {e.telefono_local}</div>}
        {!compact && e.horario && <div><span className="text-zinc-500">Horario:</span> {e.horario}</div>}
        {e.latitud && e.longitud && (
          <div className="flex items-center gap-1 text-emerald-400">
            <CheckCircle2 className="w-3 h-3" />
            <span>Geolocalizado{e.geocode_manual ? " (manual)" : ""}</span>
          </div>
        )}
      </div>
      {!compact && e.zonas?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-zinc-800">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Zonas ({e.zonas.length})</div>
          <div className="space-y-1">
            {e.zonas.map((z) => (
              <div key={z.id} className="text-xs text-zinc-300 bg-zinc-950/60 rounded px-2 py-1">
                <span className="font-medium">{z.nombre}</span>
                {z.ancho_cm && z.alto_cm && <span className="text-amber-300 ml-2">{z.ancho_cm}×{z.alto_cm}cm</span>}
                {z.requisitos_montaje && <span className="ml-2 inline-flex items-center gap-1">{z.requisitos_montaje.material_habitual && <span className="text-zinc-400">{z.requisitos_montaje.material_habitual}</span>}<RequisitosChips req={z.requisitos_montaje} testId={`zona-card-chips-${z.id}`} /></span>}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className={`flex gap-2 mt-3 pt-2 border-t border-zinc-800`}>
        <Button size="sm" variant="ghost" onClick={() => openEdit(e)} className="text-zinc-400 hover:text-amber-300 h-7 px-2">
          <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
        </Button>
        <Button size="sm" variant="ghost"
          onClick={async () => { if (window.confirm(`¿Eliminar ${e.nombre}?`)) { await api.delete(`/espacios/${e.id}`); reload(); } }}
          className="text-zinc-500 hover:text-red-400 h-7 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
}


// ============ ESPACIOS TABLE (vista hoja de cálculo) ============
function EspaciosTable({ items, cadenas, openEdit, reload }) {
  const cadName = (id) => cadenas.find((c) => c.id === id)?.nombre || "";
  // Enriquecemos cada espacio con campo `cadena_nombre` para ordenar por texto.
  const enriched = items.map((e) => ({ ...e, cadena_nombre: cadName(e.cadena_id) }));
  const { sorted, requestSort, sortIcon, sortBy } = useTableSort(enriched, { by: "nombre", dir: "asc" });

  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden" data-testid="espacios-table">
      {sorted.length === 0 ? (
        <div className="p-12 text-center text-zinc-500 text-sm">
          <MapPin className="w-10 h-10 mx-auto mb-2 text-zinc-700" />
          Sin espacios para mostrar.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/40 text-zinc-500 text-[10px] uppercase select-none sticky top-0">
              <tr>
                <Th label="Nombre"    field="nombre"        sortBy={sortBy} requestSort={requestSort} sortIcon={sortIcon} />
                <Th label="Cadena"    field="cadena_nombre" sortBy={sortBy} requestSort={requestSort} sortIcon={sortIcon} width="w-32" />
                <Th label="Tipo"      field="tipo"          sortBy={sortBy} requestSort={requestSort} sortIcon={sortIcon} width="w-28" />
                <Th label="Dirección" field="direccion"     sortBy={sortBy} requestSort={requestSort} sortIcon={sortIcon} />
                <Th label="Ciudad"    field="ciudad"        sortBy={sortBy} requestSort={requestSort} sortIcon={sortIcon} width="w-28" />
                <Th label="Provincia" field="provincia"     sortBy={sortBy} requestSort={requestSort} sortIcon={sortIcon} width="w-28" />
                <Th label="CP"        field="cp"            sortBy={sortBy} requestSort={requestSort} sortIcon={sortIcon} width="w-20" />
                <th className="text-right p-2.5 w-24">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((e) => {
                const cad = cadenas.find((c) => c.id === e.cadena_id);
                return (
                  <tr key={e.id}
                      data-testid={`espacios-table-row-${e.id}`}
                      className="border-t border-zinc-800/60 hover:bg-zinc-900/40 cursor-pointer"
                      onClick={() => openEdit(e)}>
                    <td className="p-2.5 font-medium text-zinc-100">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                        <span className="truncate">{e.nombre}</span>
                      </div>
                    </td>
                    <td className="p-2.5 text-xs">
                      {cad ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold"
                          style={{ backgroundColor: (cad.color || "#f59e0b") + "20", color: cad.color || "#f59e0b" }}>
                          {cad.nombre}
                        </span>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="p-2.5 text-zinc-300 text-xs">{e.tipo || "—"}</td>
                    <td className="p-2.5 text-zinc-300 text-xs truncate max-w-[280px]">{e.direccion || "—"}</td>
                    <td className="p-2.5 text-zinc-300 text-xs">{e.ciudad || "—"}</td>
                    <td className="p-2.5 text-zinc-400 text-xs">{e.provincia || "—"}</td>
                    <td className="p-2.5 text-zinc-400 font-mono text-xs">{e.cp || "—"}</td>
                    <td className="p-2.5 text-right">
                      <div className="inline-flex gap-1" onClick={(ev) => ev.stopPropagation()}>
                        <Button size="sm" variant="ghost"
                                onClick={() => openEdit(e)}
                                data-testid={`espacios-table-edit-${e.id}`}
                                className="h-7 w-7 p-0 text-zinc-400 hover:text-amber-300">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost"
                                onClick={async () => {
                                  if (window.confirm(`¿Eliminar ${e.nombre}?`)) {
                                    await api.delete(`/espacios/${e.id}`); reload();
                                  }
                                }}
                                data-testid={`espacios-table-delete-${e.id}`}
                                className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="bg-zinc-900/40 px-3 py-1.5 text-[11px] text-zinc-500 border-t border-zinc-800">
        Mostrando <b className="text-zinc-200">{sorted.length}</b> espacios · click en cabecera para ordenar · click en fila para editar
      </div>
    </div>
  );
}

function Th({ label, field, width, sortBy, requestSort, sortIcon }) {
  const isActive = sortBy === field;
  return (
    <th
      className={`text-left p-2.5 ${width || ""} cursor-pointer hover:bg-zinc-800/40 transition-colors ${isActive ? "text-amber-300" : ""}`}
      onClick={() => requestSort(field)}
      data-testid={`espacios-th-${field}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[9px] ${isActive ? "opacity-100" : "opacity-40"}`}>{sortIcon(field)}</span>
      </span>
    </th>
  );
}
